import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import clientReviewViewModel from '../../../view-model/client/client-review-view-model';
import ReviewForm from './review-form';
import { CheckCircle } from 'lucide-react';

interface ReviewContainerProps {
  token: string;
}

/**
 * Conteneur pour la page d'avis client
 */
const ReviewContainer: React.FC<ReviewContainerProps> = observer(({ token }) => {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  
  const verifyToken = async () => {
    await clientReviewViewModel.verifyToken(token);
    setIsInitialized(true);
  };
  
  useEffect(() => {
    verifyToken();
  }, [token]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (clientReviewViewModel.isFormValid) {
      await clientReviewViewModel.submitReview();
    }
  };
  
  // Affichage du loader pendant la vérification du token
  if (!isInitialized || clientReviewViewModel.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">

        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-gray-700">
          {t('reviews.loading')}
        </p>
      </div>
    );
  }
  
  // Affichage en cas de token invalide, expiré ou déjà utilisé
  if (!clientReviewViewModel.isTokenValid) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 max-w-md w-full">
          <h1 className="text-xl font-bold text-center text-red-600 mb-4">
            {clientReviewViewModel.isTokenExpired 
              ? t('reviews.tokenExpired') 
              : clientReviewViewModel.isTokenUsed 
                ? t('reviews.tokenUsed') 
                : t('reviews.tokenInvalid')}
          </h1>
          
          <p className="text-center text-gray-700">
            {clientReviewViewModel.isTokenExpired 
              ? t('reviews.tokenExpiredMessage') 
              : clientReviewViewModel.isTokenUsed 
                ? t('reviews.tokenUsedMessage') 
                : t('reviews.tokenInvalidMessage')}
          </p>
        </div>
      </div>
    );
  }
  
  // Affichage du formulaire si le token est valide
  if (!clientReviewViewModel.success) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full">
          <ReviewForm onSubmit={handleSubmit} />
        </div>
      </div>
    );
  }
  
  // Affichage du message de succès après soumission
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 max-w-md w-full">
        <div className="flex flex-col items-center justify-center">
          <CheckCircle className="text-green-600" size={48} />
          <h1 className="text-xl font-bold text-center text-green-600 mt-4 mb-2">
            {t('reviews.thankYou')}
          </h1>
          <p className="text-center text-gray-700">
            {t('reviews.feedbackReceived')}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ReviewContainer;
